import React, { useEffect, useMemo, useState } from 'react';
import { X, Copy, Check, Download, ExternalLink, AlertTriangle } from 'lucide-react';
import { getImageUrl } from '../../utils/getImageUrl';
import { getCategoryLabel, normalizeCategory } from '../../data/categories';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const PHONE = '+994 99 973 70 00';
const SITE = 'laliel.com';

// Kateqoriyaya görə əlavə hashtaglar
const CATEGORY_TAGS = {
  donlar: ['don', 'paltar', 'axşamdonu'],
  kostyumlar: ['kostyum', 'ofisgeyimi'],
  yubkalar: ['yubka', 'ətək'],
  pencekler: ['pencək', 'blazer'],
  'koynekler-bluzlar': ['bluz', 'köynək'],
  'tshirt-maykalar': ['tshirt', 'gündəlikgeyim'],
  'sviter-kardiqanlar': ['sviter', 'kardiqan'],
  'palto-kurtkalar': ['palto', 'gödəkçə'],
  'xudi-svitsotlar': ['xudi', 'svitşot'],
  cantalar: ['çanta', 'aksesuar'],
  kemerler: ['kəmər', 'aksesuar'],
  'serfler-yayliqlar': ['şərf', 'yaylıq'],
  zergerlik: ['zərgərlik', 'bijuteriya'],
  'gun-eynekleri': ['güneynəyi', 'aksesuar'],
  sapkalar: ['şapka', 'aksesuar'],
  'sac-aksesuarlari': ['saçaksesuarı', 'aksesuar'],
};

const BASE_TAGS = ['laliel', 'lalielbutik', 'bakı', 'onlaynbutik', 'qadıngeyimi', 'baku'];

const parseSizes = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Cloudinary URL-inə yükləmə (attachment) və JPEG bayraqlarını əlavə edir.
 * fl_attachment -> brauzer faylı açmır, birbaşa yükləyir (CORS problemi olmur).
 * f_jpg -> Instagram-ın ən etibarlı qəbul etdiyi format.
 */
const toDownloadUrl = (url, name) => {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;
  const safe = (name || 'laliel').replace(/[^a-zA-Z0-9-_]+/g, '-').toLowerCase();
  return url.replace('/upload/', `/upload/fl_attachment:${safe},f_jpg,q_90/`);
};

const buildCaption = (product) => {
  const name = typeof product.name === 'object' ? product.name.az || product.name.en : product.name;
  const desc =
    typeof product.description === 'object'
      ? product.description.az || product.description.en
      : product.description;

  const parsed = parseSizes(product.sizesStock);
  const available = parsed
    ? SIZES.filter((s) => Number(parsed[s] ?? 0) > 0)
    : [];

  const catId = normalizeCategory(product.category);
  const tags = [...BASE_TAGS, ...(CATEGORY_TAGS[catId] || [])];

  const lines = [];
  lines.push(name);
  if (desc && desc.trim()) {
    lines.push('');
    lines.push(desc.trim());
  }
  lines.push('');
  lines.push(`💰 ${Number(product.price).toFixed(2)} ${product.currency || '₼'}`);
  if (available.length > 0) {
    lines.push(`📏 Ölçülər: ${available.join(', ')}`);
  }
  if (catId) {
    lines.push(`🏷 ${getCategoryLabel(catId, 'az')}`);
  }
  lines.push('');
  lines.push(`🛍 Sifariş: ${SITE}`);
  lines.push(`💬 WhatsApp: ${PHONE}`);
  lines.push('🚚 Bakı daxili 24 saat, regionlara 2–3 gün');
  lines.push('');
  lines.push(tags.map((t) => `#${t}`).join(' '));

  return { caption: lines.join('\n'), available, name };
};

const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* aşağıdakı ehtiyat yola keç */
  }
  // Ehtiyat: köhnə brauzerlər və bəzi mobil hallar
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const InstagramPrep = ({ product, isOpen, onClose }) => {
  const initial = useMemo(() => (product ? buildCaption(product) : null), [product]);
  const [caption, setCaption] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (initial) {
      setCaption(initial.caption);
      setCopied(false);
    }
  }, [initial]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !product || !initial) return null;

  const images = Array.from(product.images || []).map((img) => getImageUrl(img));
  const soldOut = initial.available.length === 0 && parseSizes(product.sizesStock) !== null;
  const charCount = caption.length;
  const tooLong = charCount > 2200; // Instagram limiti

  const handleCopy = async () => {
    const ok = await copyText(caption);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } else {
      alert('Kopyalamaq alınmadı. Mətni əl ilə seçib kopyalayın.');
    }
  };

  const downloadOne = (url, idx) => {
    const a = document.createElement('a');
    a.href = toDownloadUrl(url, `${initial.name}-${idx + 1}`);
    a.rel = 'noopener';
    // Cloudinary deyilsə, yeni tabda aç — oradan saxlamaq olar
    if (!url.includes('res.cloudinary.com')) a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    // Brauzer bir neçə yükləməni bloklamasın deyə aralarında fasilə
    images.forEach((url, idx) => setTimeout(() => downloadOne(url, idx), idx * 600));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Başlıq */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] tracking-[0.2em] text-gray-400">INSTAGRAM ÜÇÜN</p>
            <h3 className="font-serif text-lg text-gray-900 truncate">{initial.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Bağla"
            className="flex items-center justify-center w-10 h-10 -mr-2 text-gray-400 hover:text-black rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-6">
          {soldOut && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Bu məhsulun bütün ölçüləri bitib. Paylaşmazdan əvvəl stoku yoxlayın.
            </div>
          )}

          {/* 1. Şəkillər */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                1. Şəkillər ({images.length})
              </p>
              {images.length > 1 && (
                <button
                  type="button"
                  onClick={downloadAll}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-900 hover:underline"
                >
                  <Download className="w-3.5 h-3.5" />
                  Hamısını yüklə
                </button>
              )}
            </div>

            {images.length === 0 ? (
              <p className="text-sm text-gray-400">Bu məhsulun şəkli yoxdur.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {images.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => downloadOne(url, idx)}
                    className="group relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                    aria-label={`Şəkil ${idx + 1}-i yüklə`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
                      <Download className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-2">
              Şəklə toxunun — kompüterə/telefona yüklənir.
            </p>
          </section>

          {/* 2. Mətn */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                2. Mətn
              </p>
              <span className={`text-[11px] ${tooLong ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                {charCount} / 2200
              </span>
            </div>

            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={12}
              className="w-full p-3 border border-gray-200 rounded-lg text-base sm:text-sm leading-relaxed focus:outline-none focus:border-black resize-y font-[inherit]"
            />
            <p className="text-[11px] text-gray-400 mt-2">
              Mətni burada dəyişə bilərsiniz — atmosfer üçün bir-iki cümlə əlavə etmək yaxşıdır.
            </p>
          </section>
        </div>

        {/* Düymələr */}
        <div className="flex-shrink-0 border-t border-gray-100 p-4 sm:p-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
          <a
            href="https://business.facebook.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Business Suite-i aç
          </a>

          <button
            type="button"
            onClick={handleCopy}
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-lg transition-colors ${
              copied ? 'bg-green-600 text-white' : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Kopyalandı' : 'Mətni kopyala'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstagramPrep;
